import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IBaseProps, IRecordDetailRes } from '@did/types'
import { GradientBg } from '@did/das-balance-components'
import { useDasBalanceContext } from '@did/das-app-context'
import { BaseCard, Iconfont } from '@did/uikit'
import { RECORD_STATUS } from '@did/das-balance-constants'
import {
  CKB,
  DASBalanceTokenId,
  TOKEN_ID_TO_CHAIN,
  USD
} from '@did/constants/chain'
import Decimal from 'decimal.js'
import {
  TIME_FORMAT,
  DIDPOINT_ACTIONS,
  FIAT_DECIMAL_PLACES,
  CYCLE_CALL_FUNCTION_TIME,
  TOKEN_DECIMAL_PLACES
} from '@did/constants'
import {
  formatDateTime,
  getActionSymbol,
  smartOpen,
  thousandSplit
} from '@did/tools'

export const RecordIndex: React.FC<IBaseProps> = () => {
  const { tt, router, services, connectedAccount, isProd } =
    useDasBalanceContext()
  const [recordDetail, setRecordDetail] = useState<IRecordDetailRes>({
    action_dp: '',
    amount_dp: '0',
    amount_token: '0',
    hash_dp: '',
    hash_payment: '',
    hash_withdraw: '',
    order_id: '',
    record_id: '',
    record_status: 0,
    source_payment: '',
    timestamp: 0,
    token_id: ''
  })
  let timerId = 0
  const [loading, setLoading] = useState(true)

  const viewTxHash = (hash: string) => () => {
    if (recordDetail.token_id) {
      smartOpen(
        `${TOKEN_ID_TO_CHAIN[recordDetail.token_id]?.getExplorerTrx(
          isProd
        )}${hash}`
      )
    } else {
      smartOpen(`${CKB?.getExplorerTrx(isProd)}${hash}`)
    }
  }

  const viewDpHash = (hash: string) => () => {
    smartOpen(`${CKB?.getExplorerTrx(isProd)}${hash}`)
  }

  const paymentMethod = useMemo(() => {
    if (recordDetail.token_id === USD.tokenId) {
      return 'Stripe'
    } else if (recordDetail.token_id === DASBalanceTokenId) {
      return '.bit balance'
    } else if (TOKEN_ID_TO_CHAIN[recordDetail.token_id]) {
      return TOKEN_ID_TO_CHAIN[recordDetail.token_id].name
    }
  }, [recordDetail])

  const unit = useMemo(() => {
    if (recordDetail.token_id === USD.tokenId) {
      return USD.symbol
    } else if (TOKEN_ID_TO_CHAIN[recordDetail.token_id]) {
      return TOKEN_ID_TO_CHAIN[recordDetail.token_id].symbol
    }
    return ''
  }, [recordDetail])

  const date = useMemo(() => {
    if (recordDetail.timestamp) {
      return formatDateTime(recordDetail.timestamp, TIME_FORMAT.fullDateTime)
    }
    return ''
  }, [recordDetail])

  const getActionName = (action: string) => {
    let text = ''
    switch (action) {
      case DIDPOINT_ACTIONS.burn:
        text = tt('Withdraw')
        break
      case DIDPOINT_ACTIONS.transfer_deposit:
        text = tt('Top up')
        break
      case DIDPOINT_ACTIONS.transfer_refund:
        text = tt('Refund')
        break
      case DIDPOINT_ACTIONS.transfer_tldid:
      case DIDPOINT_ACTIONS.transfer_sldid:
        text = tt('Register or Renew')
        break
      case DIDPOINT_ACTIONS.transfer_auction:
        text = tt('Dutch auction')
        break
      case DIDPOINT_ACTIONS.transfer_coupon:
        text = tt('Create coupon')
        break
    }
    return text
  }

  const stripeServiceFees = useMemo(() => {
    return new Decimal(recordDetail.amount_token).sub(recordDetail.amount_dp)
  }, [recordDetail])

  const bgType = useMemo(() => {
    if (recordDetail.record_status === RECORD_STATUS.processing) {
      return 'blue'
    }
    if (recordDetail.record_status === RECORD_STATUS.succeed) {
      return 'green'
    }
    if (recordDetail.record_status === RECORD_STATUS.failed) {
      return 'red'
    }
    return 'blue'
  }, [recordDetail])

  const fetchData = useCallback(async () => {
    if (!connectedAccount?.address || !connectedAccount?.chain?.coinType) {
      return
    }
    const res = await services.didPoint.recordDetail({
      key_info: {
        coin_type: connectedAccount?.chain?.coinType!,
        key: connectedAccount?.address!
      },
      record_id: router.query?.recordId as string,
      order_id: router.query?.orderId as string
    })
    if (res) {
      if (
        res.record_status === RECORD_STATUS.succeed ||
        res.record_status === RECORD_STATUS.failed
      ) {
        clearInterval(timerId)
      }
      setRecordDetail(res)
      setLoading(false)
    }
  }, [
    router.query?.recordId,
    router.query?.orderId,
    connectedAccount?.address,
    connectedAccount?.chain?.coinType
  ])

  useEffect(() => {
    fetchData()
    //@ts-ignore
    timerId = setInterval(() => {
      fetchData()
    }, CYCLE_CALL_FUNCTION_TIME * 3)
    return () => {
      clearInterval(timerId)
    }
  }, [connectedAccount?.address])

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-0 pt-3 z-0 relative">
      <GradientBg type={bgType} />
      {loading ? (
        <div className="flex flex-col items-center mt-20">
          <Iconfont
            className="animate-spin"
            name="loading"
            size={64}
            color="#B0B8BF"
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center mt-11">
            <div className="text-assist-font-color text-sm font-normal mb-3">
              {getActionName(recordDetail.action_dp)}
            </div>
            <div className="text-tip-font-color font-semibold text-[32px] mb-3">
              {`${getActionSymbol(recordDetail.action_dp)}${thousandSplit(
                recordDetail.amount_dp,
                FIAT_DECIMAL_PLACES
              )}`}
              <span className="text-third-font-color ml-3">Credits</span>
            </div>
            {recordDetail.record_status === RECORD_STATUS.processing ? (
              <>
                {recordDetail.action_dp ===
                  DIDPOINT_ACTIONS.transfer_deposit && (
                  <div className="text-assist-font-color text-center">
                    {tt(
                      'DIDPoint will be credited to your account within 3 minutes after the payment is successful.'
                    )}
                  </div>
                )}
                {recordDetail.action_dp === DIDPOINT_ACTIONS.burn && (
                  <div className="text-assist-font-color text-center">
                    {tt(
                      'Withdrawals are expected to take up to 15 minutes. You can check the progress of your payout at any time in the log.'
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
          <BaseCard className="mx-auto p-4 mt-6 w-full sm:w-[520px] rounded-xl !bg-border-divider-color">
            <div className="flex py-2 justify-between text-base">
              <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                {tt('Status')}
              </span>
              <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                {recordDetail.record_status === RECORD_STATUS.processing && (
                  <span className="inline-flex">
                    <Iconfont
                      className="animate-spin mr-1"
                      name="loading"
                      size={16}
                      color="#31333E"
                    />
                    {tt('Processing')}
                  </span>
                )}
                {recordDetail.record_status === RECORD_STATUS.succeed && (
                  <span>{tt('Success')}</span>
                )}
                {recordDetail.record_status === RECORD_STATUS.failed && (
                  <span className="text-[#E4B169]">{tt('Fail')}</span>
                )}
              </span>
            </div>
            {recordDetail.action_dp === DIDPOINT_ACTIONS.transfer_deposit && (
              <>
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                    {tt('Payment Method')}
                  </span>
                  <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                    {paymentMethod}
                  </span>
                </div>
                {recordDetail.source_payment && (
                  <div className="flex py-2 justify-between text-base">
                    <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                      {tt('Top-up Source')}
                    </span>
                    <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                      {recordDetail.source_payment}
                    </span>
                  </div>
                )}
                {recordDetail.token_id === USD.tokenId && (
                  <div className="flex py-2 justify-between text-base">
                    <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                      {tt('Stripe Service Fees')}
                    </span>
                    <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                      {`${thousandSplit(
                        stripeServiceFees,
                        FIAT_DECIMAL_PLACES
                      )} ${unit}`}
                    </span>
                  </div>
                )}
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                    {tt('Total Paid')}
                  </span>
                  <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                    {`${thousandSplit(
                      recordDetail.amount_token,
                      recordDetail.token_id === USD.tokenId
                        ? FIAT_DECIMAL_PLACES
                        : TOKEN_DECIMAL_PLACES
                    )} ${unit}`}
                  </span>
                </div>
              </>
            )}
            {recordDetail.action_dp === DIDPOINT_ACTIONS.burn && (
              <>
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                    {tt('DIDPoint Quantity')}
                  </span>
                  <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                    {thousandSplit(recordDetail.amount_dp, FIAT_DECIMAL_PLACES)}{' '}
                    Credits
                  </span>
                </div>
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                    {tt('Currency Quantity')}
                  </span>
                  <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                    {`${thousandSplit(
                      recordDetail.amount_token,
                      recordDetail.token_id === USD.tokenId
                        ? FIAT_DECIMAL_PLACES
                        : TOKEN_DECIMAL_PLACES
                    )} ${unit}`}
                  </span>
                </div>
                {recordDetail.hash_withdraw && (
                  <div className="flex py-2 justify-between text-base">
                    <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                      {tt('Recipient')}
                    </span>
                    <span
                      className="font-medium leading-none break-all w-1/2 text-right cursor-pointer text-link-font-color hover:text-link-hover-font-color"
                      onClick={viewTxHash(recordDetail.hash_withdraw)}
                    >
                      {recordDetail.hash_withdraw}
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="flex py-2 justify-between text-base">
              <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                {tt('Date')}
              </span>
              <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                {date}
              </span>
            </div>
            {recordDetail.order_id && (
              <div className="flex py-2 justify-between text-base">
                <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                  {tt('Order Id')}
                </span>
                <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                  {recordDetail.order_id}
                </span>
              </div>
            )}
            {recordDetail.order_id === '' && recordDetail.record_id && (
              <div className="flex py-2 justify-between text-base">
                <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8">
                  {tt('Record Id')}
                </span>
                <span className="text-tab-active font-medium leading-none break-all w-1/2 text-right">
                  {recordDetail.record_id}
                </span>
              </div>
            )}
            {recordDetail.hash_payment &&
              recordDetail.token_id !== USD.tokenId && (
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8 whitespace-nowrap break-keep mr-8">
                    {tt('Transaction Hash')}
                  </span>
                  <span
                    className="font-medium leading-none break-all w-1/2 text-right break-all cursor-pointer text-link-font-color hover:text-link-hover-font-color"
                    onClick={viewTxHash(recordDetail.hash_payment)}
                  >
                    {recordDetail.hash_payment}
                  </span>
                </div>
              )}
            {recordDetail.hash_payment === '' &&
              recordDetail.hash_withdraw === '' &&
              recordDetail.hash_dp && (
                <div className="flex py-2 justify-between text-base">
                  <span className="text-assist-font-color font-normal leading-none whitespace-nowrap break-keep mr-8 whitespace-nowrap break-keep mr-8">
                    {tt('Transaction Hash')}
                  </span>
                  <span
                    className="font-medium leading-none break-all w-1/2 text-right break-all cursor-pointer text-link-font-color hover:text-link-hover-font-color"
                    onClick={viewDpHash(recordDetail.hash_dp)}
                  >
                    {recordDetail.hash_dp}
                  </span>
                </div>
              )}
          </BaseCard>
        </>
      )}
    </div>
  )
}
